import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Eye, TriangleAlert } from 'lucide-react'

import { Card, CardHeader, CardIcon, CardTitle, CardContent, CardAction } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { CreativePreviewFrame, openPreviewInTab } from './CreativePreviewFrame'
import { PreviewApi, type PreviewLayoutParameter } from '@/services/creatives/previewApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AuthService } from '@/services/core/auth'

interface CreativePreviewCardProps {
  layoutId: string
  width: number
  height: number
  /** Component values, assets already resolved to their CDN references. */
  layoutParameters: Record<string, PreviewLayoutParameter>
  lineItemId?: string
  /** The saved creative, so "open on blank page" can use the ad server's preview page. */
  creativeId?: string
  /** Its name, for the preview window's title. */
  creativeName?: string
}

/**
 * Renders the creative as it is being edited. The ad server renders straight
 * from the layout parameters, so this reflects unsaved changes; edits are
 * debounced so a burst of typing costs one render.
 */
export function CreativePreviewCard({
  layoutId,
  width,
  height,
  layoutParameters,
  lineItemId,
  creativeId,
  creativeName,
}: CreativePreviewCardProps) {
  const { t } = useTranslation()

  // Serialise the inputs so the query key changes only on a real value change
  const signature = useMemo(
    () => JSON.stringify({ layoutId, width, height, layoutParameters }),
    [layoutId, width, height, layoutParameters]
  )
  const [debounced, setDebounced] = useState(signature)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(signature), 400)
    return () => clearTimeout(timer)
  }, [signature])

  const hasValues = Object.values(layoutParameters).some((v) => v !== null && v !== undefined && v !== '')

  const { data: html, isFetching, error } = useQuery({
    queryKey: ['creative-preview-live', debounced],
    queryFn: () =>
      PreviewApi.render({
        layoutId,
        lineItemId,
        creativeWidth: width,
        creativeHeight: height,
        layoutParameters: JSON.parse(debounced).layoutParameters,
      }),
    enabled: !!layoutId && hasValues && AuthService.isAuthenticated(),
    retry: false,
    staleTime: 60 * 1000,
  })

  return (
    <Card tone="creative">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Eye} />
          {t('creatives.preview')}
        </CardTitle>
        {(creativeId || html) && (
          <CardAction className="flex items-center gap-1">
            {creativeId ? (
              <Button type="button" variant="ghost" size="sm" asChild>
                <a
                  href={PreviewApi.blankPageUrl(creativeId, {
                    lineItemId,
                    width,
                    height,
                    name: creativeName,
                  })}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    // A plain click opens the preview window; modified clicks keep the browser's own handling
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
                    e.preventDefault()
                    PreviewApi.openWindow(e.currentTarget.href)
                  }}
                >
                  <ExternalLink className="size-3.5" />
                  {t('creatives.previewOpenBlankPage')}
                </a>
              </Button>
            ) : (
              html && (
                <Button type="button" variant="ghost" size="sm" onClick={() => openPreviewInTab(html)}>
                  <ExternalLink className="size-3.5" />
                  {t('creatives.previewOpenBlankPage')}
                </Button>
              )
            )}
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {!hasValues ? (
          <p className="text-sm text-(--ad-text-muted)">{t('creatives.previewNeedsValues')}</p>
        ) : error ? (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
          </Alert>
        ) : !html || (isFetching && !html) ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className={isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <CreativePreviewFrame
              html={html}
              width={width}
              height={height}
              maxHeight={420}
              showOpenInTab={false}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
